/**
 * <dd-reviews> — Custom reviews web component
 *
 * Fetches product reviews from the Vercel proxy that calls the Klaviyo
 * Reviews API, and posts new reviews to Klaviyo's public client endpoint
 * (POST /client/reviews) using the 6-char company_id (no private key in
 * the browser).
 *
 * Read path:  GET  {apiUrl}/reviews?product_id=<id>
 * Write path: POST https://a.klaviyo.com/client/reviews?company_id=<co>
 *
 * Attributes:
 *   data-product-id          — Shopify numeric product ID (required)
 *   data-api-url             — Reviews proxy base URL (required for read)
 *   data-klaviyo-company-id  — Klaviyo public key; omit to hide write form
 *   data-product-name        — used in the write-form product chip
 *   data-product-url         — canonical URL posted back to Klaviyo
 *   data-product-image       — optional featured image for Klaviyo dashboard
 *   data-customer-name       — pre-fills author if logged in
 *   data-customer-email      — verified email used at submit (logged-in only)
 *   data-customer-type       — raw Helium Customer Fields "professional_type"
 *                              value (e.g. "Stylist", "Salon Owner", "Student").
 *                              Blank for legacy accounts; normalizer falls
 *                              back to "Verified Stylist". Sent as
 *                              `metadata.customer_type` so review display
 *                              can render the right badge + icon later.
 *   data-customer-logged-in  — "true" | "false" — gates the submit form
 *   data-login-url           — `/account/login?return_url=<pdp>` for guests
 */
if (!customElements.get('dd-reviews')) {
  // Klaviyo endpoint/revision constants live in `dd-klaviyo-review-core.js`
  // (window.DDKlaviyoReview) so both this section and the sitewide review
  // nudge post against the same contract. Load order in theme.liquid:
  // dd-klaviyo-review-core.js → dd-reviews.js.

  class DDReviews extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.apiUrl = this.dataset.apiUrl;
      if (!this.productId || !this.apiUrl) return;

      this.klaviyoCompanyId = this.dataset.klaviyoCompanyId || '';
      this.productName = this.dataset.productName || '';
      this.productUrl = this.dataset.productUrl || '';
      this.productImage = this.dataset.productImage || '';
      this.customerName = this.dataset.customerName || '';
      this.customerEmail = this.dataset.customerEmail || '';
      this.customerType = this.dataset.customerType || '';
      this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
      this.loginUrl = this.dataset.loginUrl || '/account/login';

      this._allReviews = [];
      this._nextCursor = null;
      this._sortBy = '-created';
      this._loading = false;

      this._skeleton = this.querySelector('.dd-reviews__skeleton');
      this._body = this.querySelector('.dd-reviews__body');

      this._fetchReviews();

      // Deep-link from external surfaces (e.g. the sitewide
      // <dd-review-nudge> "Write a review" CTA) -> auto-open the write modal.
      // Independent of the reviews fetch — the modal is a write surface only,
      // so opening it before the read pipeline returns is intentional. The URL
      // param is consumed (replaceState) so back/forward don't re-open it.
      this._maybeAutoOpenWriteModal();
    }

    _maybeAutoOpenWriteModal() {
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('write_review') !== '1') return;
        url.searchParams.delete('write_review');
        var clean = url.pathname + (url.search ? url.search : '') + (url.hash || '');
        window.history.replaceState(null, '', clean);
        // Defer one frame so layout/scroll restoration settles before the
        // modal mounts and steals focus.
        requestAnimationFrame(() => this._openWriteModal());
      } catch (_) {
        /* non-fatal — auto-open is a convenience, not a requirement */
      }
    }

    async _fetchReviews(cursor) {
      if (this._loading) return;
      this._loading = true;

      const url = new URL(`${this.apiUrl}/reviews`);
      url.searchParams.set('product_id', this.productId);
      if (cursor) url.searchParams.set('page_cursor', cursor);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      try {
        const res = await fetch(url.toString(), { signal: controller.signal });
        clearTimeout(timeout);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.error) throw new Error(data.error);

        this._allReviews = this._allReviews.concat(data.reviews || []);
        this._nextCursor = data.next_cursor || null;
        this._render();
      } catch (err) {
        clearTimeout(timeout);
        if (this._allReviews.length === 0) {
          this._renderError();
        }
      } finally {
        this._loading = false;
      }
    }

    _getSorted() {
      const reviews = [...this._allReviews];
      switch (this._sortBy) {
        case '-rating':
          reviews.sort((a, b) => b.rating - a.rating);
          break;
        case 'rating':
          reviews.sort((a, b) => a.rating - b.rating);
          break;
        case 'images':
          reviews.sort((a, b) => (b.images?.length || 0) - (a.images?.length || 0));
          break;
        default:
          reviews.sort((a, b) => new Date(b.created) - new Date(a.created));
      }
      return reviews;
    }

    // Reviews API returns `images: [{ url, thumb_url }, ...]`. Legacy payloads
    // (older cached responses) may still return bare strings — handle both.
    _imgUrls(img) {
      if (typeof img === 'string') return { full: img, thumb: img };
      if (img && typeof img === 'object') {
        const full = img.url || img.thumb_url || '';
        const thumb = img.thumb_url || img.url || '';
        return { full, thumb };
      }
      return { full: '', thumb: '' };
    }

    _render() {
      if (this._skeleton) this._skeleton.style.display = 'none';
      if (!this._body) return;

      const all = this._allReviews;
      if (all.length === 0) {
        this._body.innerHTML = this._emptyHTML();
        return;
      }

      const sorted = this._getSorted();
      const allPhotos = all.flatMap((r) =>
        (r.images || []).map((img) => ({ ...this._imgUrls(img), author: r.author }))
      );

      this._body.innerHTML = `
        ${allPhotos.length > 0 ? this._photosHTML(allPhotos) : ''}
        ${this._controlsHTML(sorted.length)}
        <div class="dd-reviews__list">
          ${sorted.map((r) => this._cardHTML(r)).join('')}
        </div>
        ${this._nextCursor ? this._loadMoreHTML(sorted.length) : ''}
      `;

      this._bindEvents();
    }

    _photosHTML(photos) {
      const imgs = photos
        .slice(0, 20)
        .map(
          (p) =>
            `<img class="dd-reviews__photo" src="${this._esc(p.thumb)}" data-full="${this._esc(p.full)}" alt="Review photo by ${this._esc(p.author)}" loading="lazy" decoding="async">`
        )
        .join('');
      return `
        <div class="dd-reviews__photos">
          <div class="dd-reviews__photos-label">Review Photos</div>
          <div class="dd-reviews__photos-scroll" role="region" aria-label="Review photos" tabindex="0">${imgs}</div>
        </div>`;
    }

    _controlsHTML(total) {
      const writeBtn = this._writeCtaHTML('Write a Review', 'Login to leave review');
      return `
        <div class="dd-reviews__controls">
          <span class="dd-reviews__showing">${total} review${total !== 1 ? 's' : ''}</span>
          <div class="dd-reviews__filters">
            <select class="dd-reviews__select" data-action="sort" aria-label="Sort reviews">
              <option value="-created"${this._sortBy === '-created' ? ' selected' : ''}>Most Recent</option>
              <option value="-rating"${this._sortBy === '-rating' ? ' selected' : ''}>Highest Rated</option>
              <option value="rating"${this._sortBy === 'rating' ? ' selected' : ''}>Lowest Rated</option>
              <option value="images"${this._sortBy === 'images' ? ' selected' : ''}>With Photos</option>
            </select>
            ${writeBtn}
          </div>
        </div>`;
    }

    _canWriteReview() {
      // Write form requires the Klaviyo public key. If the merchant hasn't
      // configured it yet, we hide the CTA rather than showing a broken form.
      return !!this.klaviyoCompanyId;
    }

    _cardHTML(review) {
      const date = this._relativeDate(review.created);
      const hasLongText = (review.content || '').length > 220;
      const bodyClass = hasLongText
        ? 'dd-reviews__card-body dd-reviews__card-body--clamped'
        : 'dd-reviews__card-body';

      let imagesHTML = '';
      if (review.images?.length > 0) {
        imagesHTML = `<div class="dd-reviews__card-images">
          ${review.images
            .map((img) => {
              const { full, thumb } = this._imgUrls(img);
              return `<img class="dd-reviews__card-img" src="${this._esc(thumb)}" data-full="${this._esc(full)}" alt="Review photo by ${this._esc(review.author)}" loading="lazy" decoding="async">`;
            })
            .join('')}
        </div>`;
      }

      let replyHTML = '';
      if (review.public_reply) {
        replyHTML = `<div class="dd-reviews__card-reply">
          <div class="dd-reviews__reply-author">${this._esc(review.public_reply.author || 'Drop Dead Extensions')}</div>
          <div class="dd-reviews__reply-body">${this._esc(review.public_reply.content)}</div>
        </div>`;
      }

      const initial = (review.author || '?').trim().charAt(0).toUpperCase();

      // Drop Dead's brand is pro-only — anyone who can reach a product page,
      // purchase, and submit a review has already cleared the stylist approval
      // gate. So every card gets a verified chip. Klaviyo's public reviews
      // API doesn't accept ad-hoc metadata on a review record, so we can't
      // persist the submitter's professional_type alongside the review and
      // every chip normalizes to the baseline "Verified Stylist" label —
      // the credential every approved Drop Dead account holds. If we later
      // enrich reviews via a sidecar channel (Klaviyo profile/event), the
      // `review.customer_type` / `review.metadata.customer_type` reads below
      // will pick it up automatically without UI changes. The helper lives
      // in `dd-klaviyo-review-core.js` so the PDP and the sitewide review
      // nudge always agree on the icon + label.
      const rawType =
        (review.metadata && review.metadata.customer_type) ||
        review.customer_type ||
        '';
      // Inline fallback badge-check glyph for defense-in-depth: if the core
      // helper hasn't loaded (network flake, blocker, stale cache), the
      // badge still renders with an icon instead of a text-only chip. This
      // mirrors `DDKlaviyoReview.VERIFIED_BADGE_ICON` in dd-klaviyo-review-core.js.
      const fallbackIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></svg>';
      const badge =
        (window.DDKlaviyoReview && window.DDKlaviyoReview.verifiedBadge)
          ? window.DDKlaviyoReview.verifiedBadge(rawType)
          : { key: 'stylist', label: 'Verified Stylist', iconSVG: fallbackIcon };
      const badgeHTML = `<span class="dd-reviews__card-verified dd-reviews__card-verified--${badge.key}" title="${this._esc(badge.label)}">
        ${badge.iconSVG}
        <span class="dd-reviews__card-verified-label">${this._esc(badge.label)}</span>
      </span>`;

      return `
        <article class="dd-reviews__card" aria-label="Review by ${this._esc(review.author)}, ${review.rating} stars">
          <header class="dd-reviews__card-head">
            <div class="dd-reviews__card-avatar" aria-hidden="true">${this._esc(initial)}</div>
            <div class="dd-reviews__card-meta">
              <div class="dd-reviews__card-name-row">
                <span class="dd-reviews__card-name">${this._esc(review.author)}</span>
                ${badgeHTML}
              </div>
              <div class="dd-reviews__card-sub">
                <span class="dd-reviews__card-stars">${this._starsHTML(review.rating, 14)}</span>
                <span class="dd-reviews__card-date">${date}</span>
              </div>
            </div>
          </header>
          ${review.title ? `<div class="dd-reviews__card-title">${this._esc(review.title)}</div>` : ''}
          <div class="${bodyClass}">${this._esc(review.content)}</div>
          ${hasLongText ? '<button class="dd-reviews__read-more" data-action="toggle-text" aria-expanded="false">Read more</button>' : ''}
          ${imagesHTML}
          ${replyHTML}
        </article>`;
    }

    _loadMoreHTML(shown) {
      return `
        <div class="dd-reviews__load-more-wrap">
          <button class="dd-reviews__load-more" data-action="load-more">Load More Reviews</button>
          <div class="dd-reviews__pagination-count">Showing ${shown} reviews</div>
        </div>`;
    }

    _emptyHTML() {
      const stars = Array.from({ length: 5 }, () =>
        `<svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z"/></svg>`
      ).join('');
      const writeBtn = this._writeCtaHTML(
        'Write the first review',
        'Login to leave review',
        'dd-reviews__write-btn--empty'
      );
      return `
        <div class="dd-reviews__empty">
          <div class="dd-reviews__empty-stars">${stars}</div>
          <div class="dd-reviews__empty-heading">No reviews yet</div>
          <div class="dd-reviews__empty-body">Be the first to share your experience.</div>
          ${writeBtn}
        </div>`;
    }

    /**
     * Build the write-a-review CTA. Logged-in pros go straight to the write
     * modal (`data-action="open-write"`). Guests instead open the sitewide
     * registration/login iframe overlay via `data-registration-overlay-open`
     * + `data-overlay-path="/login"` — the same attributes every other guest-
     * gated CTA on the site uses (PDP buy buttons, cart drawer sign-in, etc.).
     * `dd-registration-overlay.js` installs a single document-level delegated
     * click listener that intercepts those attributes and opens the iframe at
     * `apply.dropdeadextensions.com/login`, so no wiring is needed here and
     * the iframe is typically prefetched on idle before the click. Returns
     * empty string if Klaviyo isn't configured yet, since neither path can
     * succeed.
     */
    _writeCtaHTML(loggedInLabel, guestLabel, extraClass) {
      if (!this._canWriteReview()) return '';
      const cls = `dd-reviews__write-btn${extraClass ? ' ' + extraClass : ''}`;
      if (this.customerLoggedIn) {
        // Gate repeat submissions on this PDP per the one-review-per-
        // customer-per-order-item policy. The PDP submit path doesn't
        // carry an order context, so the ledger check is product-scoped
        // (any stored entry for this product trips the gate). A "Submit
        // another" escape hatch stays available for the honest case where
        // the first POST didn't actually land in Klaviyo's moderation
        // queue — the gate is a hint, not a hard lock.
        const core = window.DDKlaviyoReview;
        const alreadyReviewed =
          core &&
          typeof core.wasReviewSubmitted === 'function' &&
          core.wasReviewSubmitted({ productId: this.productId });
        if (alreadyReviewed) {
          return `
            <div class="dd-reviews__already-reviewed">
              <span class="dd-reviews__already-reviewed-chip" role="status">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></svg>
                Review submitted — awaiting moderation
              </span>
              <button type="button" class="dd-reviews__already-reviewed-link" data-action="open-write">Submit another</button>
            </div>`;
        }
        return `<button type="button" class="${cls}" data-action="open-write">${this._esc(loggedInLabel)}</button>`;
      }
      return `<button type="button" class="${cls}" data-registration-overlay-open data-overlay-path="/login" aria-haspopup="dialog">${this._esc(guestLabel)}</button>`;
    }

    _renderError() {
      if (this._skeleton) this._skeleton.style.display = 'none';
      if (this._body) {
        this._body.innerHTML = '<div class="dd-reviews__error">Reviews temporarily unavailable.</div>';
      }
    }

    _starsHTML(rating, size) {
      return Array.from({ length: 5 }, (_, i) => {
        const n = i + 1;
        let fill = Math.min(100, Math.max(0, (rating - n + 1) * 100));
        const filled = fill >= 100 ? '#1c1917' : (fill <= 0 ? '#d6d3d1' : null);
        if (filled) {
          return `<svg width="${size}" height="${size}" viewBox="0 0 20 20"><path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z" fill="${filled}"/></svg>`;
        }
        const id = `rs${size}${n}${Math.random().toString(36).slice(2, 6)}`;
        return `<svg width="${size}" height="${size}" viewBox="0 0 20 20">
          <defs><linearGradient id="${id}"><stop offset="${fill}%" stop-color="#1c1917"/><stop offset="${fill}%" stop-color="#d6d3d1"/></linearGradient></defs>
          <path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z" fill="url(#${id})"/></svg>`;
      }).join('');
    }

    _relativeDate(iso) {
      if (!iso) return '';
      const diff = Date.now() - new Date(iso).getTime();
      const days = Math.floor(diff / 86400000);
      if (days < 1) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days} days ago`;
      if (days < 30) { const w = Math.floor(days / 7); return `${w} week${w > 1 ? 's' : ''} ago`; }
      if (days < 365) { const m = Math.floor(days / 30); return `${m} month${m > 1 ? 's' : ''} ago`; }
      const y = Math.floor(days / 365);
      return `${y} year${y > 1 ? 's' : ''} ago`;
    }

    _esc(str) {
      if (!str) return '';
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }

    _bindEvents() {
      this.querySelectorAll('[data-action="sort"]').forEach((el) => {
        el.addEventListener('change', (e) => {
          this._sortBy = e.target.value;
          this._render();
        });
      });

      this.querySelectorAll('[data-action="toggle-text"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const body = btn.previousElementSibling;
          if (!body) return;
          const expanded = body.classList.toggle('dd-reviews__card-body--expanded');
          body.classList.toggle('dd-reviews__card-body--clamped', !expanded);
          btn.textContent = expanded ? 'Show less' : 'Read more';
          btn.setAttribute('aria-expanded', String(expanded));
        });
      });

      this.querySelectorAll('[data-action="load-more"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!this._nextCursor) return;
          btn.disabled = true;
          this._fetchReviews(this._nextCursor);
        });
      });

      this.querySelectorAll('.dd-reviews__photo, .dd-reviews__card-img').forEach((img) => {
        img.addEventListener('click', () => {
          // Prefer full-resolution source for the lightbox; fall back to src.
          const full = img.getAttribute('data-full') || img.src;
          this._openLightbox(full);
        });
      });

      this.querySelectorAll('[data-action="open-write"]').forEach((btn) => {
        btn.addEventListener('click', () => this._openWriteModal());
      });
    }

    /* ── Write-a-review modal ───────────────────────────────── */

    _openWriteModal() {
      if (!this._canWriteReview()) return;
      if (this._writeModal) return; // already open

      const modal = document.createElement('div');
      modal.className = 'dd-reviews__modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'dd-reviews-modal-title');
      // Guests get a sign-in prompt instead of the form. Submission is gated
      // to logged-in customers so we can trust the verified email from the
      // Shopify session (prevents review spoofing) — Drop Dead's storefront
      // is pro-only anyway, so the account is what earns the "Verified
      // Stylist" badge.
      modal.innerHTML = this.customerLoggedIn
        ? this._writeFormHTML()
        : this._signInPromptHTML();

      document.body.appendChild(modal);
      this._writeModal = modal;
      // Prevent the page from scrolling behind the modal on mobile.
      this._prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      this._bindWriteModalEvents();
      // Move focus into the dialog for keyboard + screen-reader users.
      const first = modal.querySelector(
        this.customerLoggedIn ? '[data-dd-rv-rating] button' : '[data-dd-rv-signin]'
      );
      if (first) first.focus();
    }

    _signInPromptHTML() {
      return `
        <div class="dd-reviews__modal-backdrop" data-dd-rv-backdrop></div>
        <div class="dd-reviews__modal-panel dd-reviews__modal-panel--compact">
          <button type="button" class="dd-reviews__modal-close" data-dd-rv-close aria-label="Close">&times;</button>
          <div class="dd-reviews__signin">
            <svg class="dd-reviews__signin-icon" viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="2"/>
              <path d="M24 14v10l6 4" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="dd-reviews__modal-title" id="dd-reviews-modal-title">Sign in to write a review</h3>
            <p class="dd-reviews__modal-subtitle">
              Reviews on Drop Dead are written by verified stylists. Sign in to your pro account to leave a review for <strong>${this._esc(this.productName)}</strong>.
            </p>
            <div class="dd-reviews__signin-actions">
              <a href="${this._esc(this.loginUrl)}" class="dd-reviews__form-submit" data-dd-rv-signin>Sign in</a>
              <button type="button" class="dd-reviews__form-cancel" data-dd-rv-close>Not now</button>
            </div>
          </div>
        </div>
      `;
    }

    _closeWriteModal() {
      if (!this._writeModal) return;
      const modal = this._writeModal;
      // Detach references immediately so a second call (rapid double
      // tap on the close button, parallel ESC key) can't kick off a
      // second exit animation against a node already mid-tear-down.
      this._writeModal = null;
      document.body.style.overflow = this._prevBodyOverflow || '';

      // Honour reduced-motion: skip the exit animation entirely.
      const reducedMotion =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) {
        modal.remove();
        return;
      }

      // Play the reverse animation defined in dd-reviews.css under
      // `.dd-reviews__modal.is-closing` (fade-out backdrop + pop-out
      // panel, ~160ms with a sharp ease-in). Listen for animationend
      // on the panel — if for any reason the event never fires
      // (animation rule stripped, browser bug, GPU stall) a 220ms
      // safety timeout still removes the node.
      modal.classList.add('is-closing');
      const panel = modal.querySelector('.dd-reviews__modal-panel');
      let removed = false;
      const finish = () => {
        if (removed) return;
        removed = true;
        modal.remove();
      };
      if (panel) {
        panel.addEventListener('animationend', finish, { once: true });
      }
      setTimeout(finish, 220);
    }

    _writeFormHTML() {
      const starBtns = Array.from({ length: 5 }, (_, i) => {
        const n = i + 1;
        return `<button type="button" data-rating="${n}" aria-label="${n} star${n > 1 ? 's' : ''}">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z" fill="currentColor"/></svg>
        </button>`;
      }).join('');

      // Form is only rendered when the customer is logged in, so we can
      // surface the verified identity as a read-only chip instead of asking
      // for email again. Name stays editable as a display name since some
      // pros use a salon handle.
      const identityChip = `<p class="dd-reviews__form-note">Submitting as <strong>${this._esc(this.customerEmail)}</strong>. Your review will appear after moderation.</p>`;

      return `
        <div class="dd-reviews__modal-backdrop" data-dd-rv-backdrop></div>
        <div class="dd-reviews__modal-panel">
          <button type="button" class="dd-reviews__modal-close" data-dd-rv-close aria-label="Close">&times;</button>
          <h3 class="dd-reviews__modal-title" id="dd-reviews-modal-title">Write a Review</h3>
          <p class="dd-reviews__modal-subtitle">${this._esc(this.productName)}</p>

          <form class="dd-reviews__form" data-dd-rv-form novalidate>
            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label">Your rating <span aria-hidden="true">*</span></label>
              <div class="dd-reviews__rating-picker" data-dd-rv-rating role="radiogroup" aria-label="Star rating">
                ${starBtns}
              </div>
              <input type="hidden" name="rating" value="" data-dd-rv-rating-value required>
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-title">Review title</label>
              <input type="text" id="dd-rv-title" name="title" maxlength="120" class="dd-reviews__form-input" placeholder="Sums up your experience in a few words">
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-content">Your review <span aria-hidden="true">*</span></label>
              <textarea id="dd-rv-content" name="content" class="dd-reviews__form-textarea" rows="5" maxlength="4000" placeholder="Tell other stylists what you loved — or didn't — about this product." required></textarea>
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-author">Display name <span aria-hidden="true">*</span></label>
              <input type="text" id="dd-rv-author" name="author" maxlength="120" class="dd-reviews__form-input" value="${this._esc(this.customerName)}" required>
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-photos">Add photos <span class="dd-reviews__form-optional">(optional, up to 4)</span></label>
              <input
                type="file"
                id="dd-rv-photos"
                class="dd-reviews__photo-input"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                data-dd-rv-photo-input
              >
              <label for="dd-rv-photos" class="dd-reviews__photo-drop" data-dd-rv-photo-drop>
                <svg class="dd-reviews__photo-drop-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                <span class="dd-reviews__photo-drop-title">Add photos</span>
                <span class="dd-reviews__photo-drop-hint">JPEG, PNG, WebP or HEIC · 15&nbsp;MB max each</span>
              </label>
              <ul class="dd-reviews__photo-list" data-dd-rv-photo-list></ul>
            </div>

            ${identityChip}

            <div class="dd-reviews__form-error" data-dd-rv-error role="alert" hidden></div>

            <div class="dd-reviews__form-actions">
              <button type="button" class="dd-reviews__form-cancel" data-dd-rv-close>Cancel</button>
              <button type="submit" class="dd-reviews__form-submit" data-dd-rv-submit>Submit review</button>
            </div>
          </form>

          <div class="dd-reviews__form-success" data-dd-rv-success hidden>
            <svg class="dd-reviews__success-icon" viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="2"/>
              <path d="M14 24l7 7 13-14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h4 class="dd-reviews__success-title">Thanks for your review!</h4>
            <p class="dd-reviews__success-body">It's been submitted to our team and will appear on the page after approval.</p>
            <button type="button" class="dd-reviews__form-submit" data-dd-rv-close>Close</button>
          </div>
        </div>
      `;
    }

    _bindWriteModalEvents() {
      const modal = this._writeModal;
      if (!modal) return;

      const close = () => this._closeWriteModal();
      modal.querySelectorAll('[data-dd-rv-close], [data-dd-rv-backdrop]').forEach((el) => {
        el.addEventListener('click', close);
      });
      this._escHandler = (e) => {
        if (e.key === 'Escape') close();
      };
      document.addEventListener('keydown', this._escHandler);

      // Star picker — hover + click sync to hidden input.
      const picker = modal.querySelector('[data-dd-rv-rating]');
      const ratingValue = modal.querySelector('[data-dd-rv-rating-value]');
      if (picker && ratingValue) {
        const setVisual = (n) => {
          picker.querySelectorAll('button').forEach((btn) => {
            const v = parseInt(btn.dataset.rating, 10);
            btn.classList.toggle('is-filled', v <= n);
          });
        };
        picker.addEventListener('mouseover', (e) => {
          const btn = e.target.closest('button[data-rating]');
          if (btn) setVisual(parseInt(btn.dataset.rating, 10));
        });
        picker.addEventListener('mouseleave', () => {
          setVisual(parseInt(ratingValue.value, 10) || 0);
        });
        picker.addEventListener('click', (e) => {
          const btn = e.target.closest('button[data-rating]');
          if (!btn) return;
          ratingValue.value = btn.dataset.rating;
          setVisual(parseInt(btn.dataset.rating, 10));
        });
      }

      // Photo uploader — accumulate File objects on the instance so the
      // user can add photos in multiple batches (native <input multiple>
      // replaces the FileList each time) and remove individual ones before
      // submitting. Files stay in memory only; they're converted to JPEG
      // data URIs at submit time via DDKlaviyoReview.filesToImages.
      this._pendingPhotos = [];
      const photoInput = modal.querySelector('[data-dd-rv-photo-input]');
      const photoList = modal.querySelector('[data-dd-rv-photo-list]');
      const photoDrop = modal.querySelector('[data-dd-rv-photo-drop]');
      const MAX_PHOTOS = 4;

      if (photoInput && photoList) {
        photoInput.addEventListener('change', () => {
          const incoming = Array.from(photoInput.files || []);
          if (!incoming.length) return;
          const slots = MAX_PHOTOS - this._pendingPhotos.length;
          if (slots <= 0) {
            photoInput.value = '';
            return;
          }
          incoming.slice(0, slots).forEach((file) => this._pendingPhotos.push(file));
          photoInput.value = ''; // allow re-picking the same file if removed
          this._renderPhotoList(photoList);
        });

        photoList.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-dd-rv-photo-remove]');
          if (!btn) return;
          const idx = parseInt(btn.dataset.ddRvPhotoRemove, 10);
          if (Number.isNaN(idx)) return;
          this._pendingPhotos.splice(idx, 1);
          this._renderPhotoList(photoList);
        });
      }

      // Basic drag-and-drop onto the drop label. The native file input
      // handles click + keyboard; this just lets users drop images from
      // Finder/Explorer without opening a picker.
      if (photoDrop && photoInput) {
        ['dragenter', 'dragover'].forEach((evt) => {
          photoDrop.addEventListener(evt, (e) => {
            e.preventDefault();
            photoDrop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach((evt) => {
          photoDrop.addEventListener(evt, (e) => {
            e.preventDefault();
            photoDrop.classList.remove('is-dragover');
          });
        });
        photoDrop.addEventListener('drop', (e) => {
          const files = e.dataTransfer && e.dataTransfer.files;
          if (!files || !files.length) return;
          const slots = MAX_PHOTOS - this._pendingPhotos.length;
          if (slots <= 0) return;
          Array.from(files).slice(0, slots).forEach((file) => {
            if (file && file.type && file.type.startsWith('image/')) {
              this._pendingPhotos.push(file);
            }
          });
          this._renderPhotoList(photoList);
        });
      }

      const form = modal.querySelector('[data-dd-rv-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this._submitReview(form);
        });
      }
    }

    _renderPhotoList(listEl) {
      if (!listEl) return;
      // Revoke previous object URLs before re-rendering to avoid leaking
      // blob: handles across add/remove cycles.
      listEl.querySelectorAll('img[data-object-url]').forEach((img) => {
        try { URL.revokeObjectURL(img.src); } catch (_) { /* noop */ }
      });
      listEl.innerHTML = this._pendingPhotos
        .map((file, i) => {
          const url = URL.createObjectURL(file);
          const name = this._esc(file.name || `Photo ${i + 1}`);
          return `<li class="dd-reviews__photo-item">
            <img src="${url}" alt="${name}" data-object-url>
            <button type="button" class="dd-reviews__photo-remove" data-dd-rv-photo-remove="${i}" aria-label="Remove ${name}">&times;</button>
          </li>`;
        })
        .join('');
    }

    disconnectedCallback() {
      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }
    }

    async _submitReview(form) {
      const errorEl = form.querySelector('[data-dd-rv-error]');
      const submitBtn = form.querySelector('[data-dd-rv-submit]');
      const hideError = () => { errorEl.hidden = true; errorEl.textContent = ''; };
      const showError = (msg) => { errorEl.hidden = false; errorEl.textContent = msg; };
      hideError();

      // Defence-in-depth: the form should never render for guests (see
      // `_openWriteModal`), but if someone tampered with the DOM or the
      // session expired mid-session, refuse to submit rather than POST an
      // unverified email to Klaviyo.
      if (!this.customerLoggedIn || !this.customerEmail) {
        showError('Please sign in to submit a review.');
        return;
      }

      const fd = new FormData(form);
      const rating = parseInt(fd.get('rating') || '', 10);
      const author = (fd.get('author') || '').toString().trim();
      const title = (fd.get('title') || '').toString().trim();
      const content = (fd.get('content') || '').toString().trim();
      // Email always comes from the verified Shopify session — never from user
      // input — so reviewers can't impersonate another customer's address.
      const email = this.customerEmail;

      if (!rating || rating < 1 || rating > 5) { showError('Please select a star rating.'); return; }
      if (!author) { showError('Please enter a display name.'); return; }
      if (!content || content.length < 10) { showError('Please write at least a sentence (10+ characters).'); return; }

      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';

      // Delegate the POST shape + 202/4xx/429 handling to the shared core so
      // this section and the sitewide review nudge speak one Klaviyo dialect.
      // Loaded via <script src="dd-klaviyo-review-core.js"> in layout/theme.liquid
      // before this file; if it's missing we fall back to an inline submission
      // error rather than silently doing the wrong thing.
      const core = window.DDKlaviyoReview;
      if (!core || typeof core.postKlaviyoReview !== 'function') {
        showError('Review service is still loading. Please try again in a moment.');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit review';
        return;
      }

      // Encode any attached photos to JPEG data URIs in-browser. Klaviyo's
      // reviews endpoint accepts the encoded bytes directly on create, so
      // the files never hit our server. `filesToImages` throws on quota /
      // unsupported mime; surface those as inline form errors rather than
      // failing the whole POST.
      let images;
      if (Array.isArray(this._pendingPhotos) && this._pendingPhotos.length) {
        submitBtn.textContent = 'Processing photos…';
        try {
          images = await core.filesToImages(this._pendingPhotos, { maxCount: 4 });
        } catch (err) {
          showError((err && err.message) || 'Could not process one of your photos.');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit review';
          return;
        }
        submitBtn.textContent = 'Submitting…';
      }

      // Klaviyo's public `client/reviews` endpoint rejects any attribute
      // outside its documented schema, so we don't attach ad-hoc metadata
      // (source / customer_type) to the review itself. Badge flavor is
      // derived at display time from the fallback path — see
      // `verifiedBadge()` in dd-klaviyo-review-core.js. If we need submitter
      // professional_type persisted per review in the future it needs to
      // ride a separate Klaviyo event/profile channel.
      const result = await core.postKlaviyoReview({
        companyId: this.klaviyoCompanyId,
        email,
        author,
        title,
        content,
        rating,
        productId: this.productId,
        images,
      });

      if (result.ok) {
        // Record the submission in the shared ledger so the "Write a review"
        // button on subsequent opens (or other tabs) renders as an
        // "awaiting approval" chip instead of re-prompting. The PDP submit
        // path doesn't carry an orderId, so the entry is stored under the
        // productId-only slot and any future order-scoped check matches
        // via the product prefix.
        if (typeof core.markReviewSubmitted === 'function') {
          core.markReviewSubmitted({
            productId: this.productId,
            rating,
            source: 'pdp',
          });
        }
        this._showWriteSuccess();
        // Re-render the surrounding reviews section so the "Write a
        // review" button flips to the "awaiting moderation" chip as soon
        // as the modal is dismissed. The modal is appended to <body>, not
        // to this element, so re-rendering here won't disturb the
        // currently-visible success panel.
        try { this._render(); } catch (_) { /* defensive; render errors shouldn't swallow submit success */ }
        // Tell any listeners (analytics, the sitewide review nudge, etc.) a
        // review was submitted. Bubbles to document so cross-surface listeners
        // (e.g. `<dd-review-nudge>` on the same tab) can dedupe by productId.
        this.dispatchEvent(new CustomEvent('dd-review:submitted', {
          bubbles: true,
          detail: {
            productId: this.productId,
            orderId: this.dataset.orderId || null,
            rating,
            source: 'pdp',
          },
        }));
        return;
      }

      if (result.rateLimited) {
        showError('Too many attempts right now. Please try again in a moment.');
      } else if (result.error === 'network' || !result.res) {
        showError('Could not reach the review service. Please try again in a moment.');
      } else {
        showError(result.error || 'Submission failed. Please try again.');
      }
      submitBtn.disabled = false;
      submitBtn.textContent = 'Submit review';
    }

    _showWriteSuccess() {
      const modal = this._writeModal;
      if (!modal) return;
      const form = modal.querySelector('[data-dd-rv-form]');
      const success = modal.querySelector('[data-dd-rv-success]');
      if (form) form.hidden = true;
      if (!success) return;
      success.hidden = false;

      // The modal panel is `overflow-y: auto` with `max-height: 95vh` on
      // mobile, so if the submitter scrolled down to find the submit button
      // in a long form, the panel's scrollTop is now parked below the short
      // success card. Snap the panel back to the top and pull the success
      // element into view. Both paths are idempotent; running them together
      // covers nested scrollers we don't directly own.
      const panel = success.closest('.dd-reviews__modal-panel');
      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReducedMotion ? 'auto' : 'smooth';
      if (panel && typeof panel.scrollTo === 'function') {
        panel.scrollTo({ top: 0, behavior });
      } else if (panel) {
        panel.scrollTop = 0;
      }
      try {
        success.scrollIntoView({ block: 'start', behavior });
      } catch (_) {
        success.scrollIntoView(true);
      }
    }

    _openLightbox(src) {
      const lb = document.createElement('div');
      lb.className = 'dd-reviews__lightbox';
      lb.innerHTML = `
        <button class="dd-reviews__lightbox-close" aria-label="Close">&times;</button>
        <img src="${this._esc(src)}" alt="Review photo">`;
      lb.addEventListener('click', e => {
        if (e.target === lb || e.target.closest('.dd-reviews__lightbox-close')) lb.remove();
      });
      document.addEventListener('keydown', function handler(e) {
        if (e.key === 'Escape') { lb.remove(); document.removeEventListener('keydown', handler); }
      });
      document.body.appendChild(lb);
    }
  }

  customElements.define('dd-reviews', DDReviews);
}
